import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbSchema } from "@/lib/schema";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { TRACKED_CARD_COUNT } from "@/lib/prices-shared";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";

const URL = "https://palstars.com/palworld-tcg";
const TITLE = "Palworld TCG: Rules, Sets, Prices & Products";
const DESCRIPTION =
  "Palworld Official Card Game answers in one place: how to play, the set list and card numbers, booster box and trial deck value, rarity symbols and card prices.";

const FAQ: FaqItem[] = [
  {
    q: "What is the Palworld TCG?",
    a: "The Palworld Official Card Game is the trading card game based on Palworld, published by Bushiroad. Cards are printed in booster sets (EBP), trial decks (ETD), promotional printings (EPR) and Soul cards (ESOUL), and matches are played by powering Pals with energy and knocking out your opponent's Pals.",
  },
  {
    q: "Where do I start with the Palworld card game?",
    a: "Read how to play first, then buy a single trial deck — it is legal out of the box and enough for a full match. Once you know the turn structure, use the deck builder to plan upgrades before buying singles.",
  },
  {
    q: "How much are Palworld TCG cards worth?",
    a: `Pal Stars tracks ${TRACKED_CARD_COUNT} printings with a daily validated marketplace sync, so each card has its own market value, price history, listing depth and liquidity score rather than a single blanket estimate.`,
  },
  {
    q: "Is the Palworld TCG worth collecting?",
    a: "It is a young line, which means base sets are still affordable and parallel printings are genuinely scarce. Collect by printed code, sleeve chase pulls immediately, and check liquidity before paying a premium on a thin printing.",
  },
];

const TOOLS: { to: string; label: string; note: string }[] = [
  { to: "/cards", label: "Palworld TCG card list", note: "Every printing with its official scan and printed code." },
  { to: "/prices", label: "Palworld card price chart", note: "Daily market value and full price history per card." },
  { to: "/tcg-price-index", label: "Palworld TCG price index", note: "Market benchmark with per-card liquidity scores." },
  { to: "/deck-builder", label: "Palworld deck builder", note: "Build, cost-curve and save legal decks." },
  { to: "/marketplace", label: "Palworld TCG sealed prices", note: "Booster boxes, packs, trial decks and accessories." },
  { to: "/collection", label: "Palworld collection tracker", note: "Buy, sell and profit tracking per card." },
];

/** Slim card list for the hub — keeps the full topic corpus out of the entry bundle. */
async function topicCards() {
  const { TCG_PAGES } = await import("@/lib/tcg-pages");
  return TCG_PAGES.map((p) => ({ slug: p.slug, h1: p.h1, description: p.description }));
}

export const Route = createFileRoute("/palworld-tcg/")({
  loader: async () => ({ pages: await topicCards() }),
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG — rules, sets, prices and products" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld TCG — Pal Stars" },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Palworld TCG", "/palworld-tcg"]]),
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${URL}#page`,
          url: URL,
          name: TITLE,
          description: DESCRIPTION,
          isPartOf: { "@type": "WebSite", name: "Pal Stars", url: "https://palstars.com" },
          hasPart: [
            ...(loaderData?.pages ?? []).map((p) => ({ "@type": "WebPage", name: p.h1, url: `${URL}/${p.slug}` })),
            ...TOOLS.map((t) => ({ "@type": "WebPage", name: t.label, url: `https://palstars.com${t.to}` })),
          ],
        }),
      },
    ],
  }),
  component: TcgHub,
});

function TcgHub() {
  const { pages } = Route.useLoaderData();
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <h1 className="text-3xl sm:text-4xl font-bold">Palworld TCG</h1>
      <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
        The Palworld Official Card Game, explained one question at a time. Each page below answers a single thing —
        how a turn works, how card numbers and rarity suffixes are structured, whether a booster box beats singles,
        which trial deck to buy, and how the daily card prices on this site are calculated. Pal Stars tracks{" "}
        {TRACKED_CARD_COUNT} printings with a validated marketplace sync every day.
      </p>
      <ContextVisualStrip variant="tcg" />
      <p className="mt-3 text-sm">
        <Link
          to="/guides/$slug"
          params={{ slug: "palworld-tcg-sleeve-card-set-vol-1" }}
          className="font-semibold text-primary hover:underline"
        >
          New: Palworld Sleeve &amp; Card Set Vol.1 card list and parallel foil guide →
        </Link>
      </p>

      <section className="mt-10">
        <h2 className="text-xl font-bold">Palworld TCG answers</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {pages.map((p) => (
            <li key={p.slug} className="glass rounded-2xl p-5">
              <Link
                to="/palworld-tcg/$topic"
                params={{ topic: p.slug }}
                className="text-base font-semibold text-primary hover:underline"
              >
                {p.h1}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{p.description}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="text-xl font-bold">Live card game tools</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOOLS.map((t) => (
            <li key={t.to} className="rounded-lg border border-border/60 p-3">
              <Link to={t.to} className="text-sm font-semibold text-primary hover:underline">
                {t.label}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{t.note}</p>
            </li>
          ))}
        </ul>
      </section>

      <FaqSection items={FAQ} heading="Palworld TCG FAQ" />
      <InternalLinkBlock path="/palworld-tcg" />
    </div>
  );
}
